<template>
  <div
    :class="[
      'notification-item p-4 mb-3 shadow-sm transition-colors duration-200 relative overflow-hidden',
      { 'cursor-pointer': true },
      notification.read ? 'bg-surface-100 dark:bg-surface-800/60' : 'bg-surface-50 dark:bg-surface-700',
      categoryClasses,
    ]"
    role="article"
    aria-labelledby="notification-title"
    @click="handleClick"
  >
    <!-- Left border accent with glow effect -->
    <div
      :class="[
        'absolute left-0 top-0 bottom-0 w-1',
        !notification.read ? 'notification-glow' : '',
        getBorderClass(),
      ]"
    />

    <!-- Content with padding for the left border -->
    <div class="pl-3">
      <!-- Title on its own line -->
      <div class="flex justify-between items-start">
        <h3
          id="notification-title"
          class="font-semibold text-surface-800 dark:text-surface-100 leading-tight text-base"
          :class="{ 'font-bold': !notification.read }"
        >
          {{ notification.title }}

          <!-- High priority indicator -->
          <span
            v-if="notification.priority === 'high'"
            class="inline-flex items-center ml-2 text-xs text-red-600 dark:text-red-400 mt-4"
            aria-label="High priority"
          >
            <i class="pi pi-exclamation-circle mr-1" />
            Important
          </span>
        </h3>

        <!-- Unread indicator dot -->
        <div
          v-if="!notification.read"
          class="w-2 h-2 rounded-full bg-primary-500 notification-pulse ml-2"
          aria-label="Unread notification"
        />
      </div>

      <!-- Metadata line (timestamp and category icon) -->
      <div class="flex items-center mt-2 mb-2 text-xs text-surface-500 dark:text-surface-400">
        <!-- Category Icon -->
        <i
          :class="['pi', categoryIcon, 'mr-2 text-sm',
                   { 'text-surface-400 dark:text-surface-400': notification.read,
                     'text-primary-600 dark:text-primary-500': !notification.read }]"
          aria-hidden="true"
        />

        <!-- Timestamp -->
        <span
          class="whitespace-nowrap"
          :title="notification.createdAt?.toLocaleString()"
        >
          {{ formattedTime }}
        </span>
      </div>

      <!-- Notification Body -->
      <p
        class="text-sm text-surface-600 dark:text-surface-300 mt-2 mb-0 leading-relaxed"
        :class="{ 'line-clamp-2': compact }"
      >
        {{ notification.body }}
      </p>

      <!-- Action Buttons -->
      <div class="flex justify-end mt-3 gap-2">
        <button
          v-if="!notification.read"
          class="text-xs px-3 py-1 rounded text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 tech-button"
          aria-label="Mark as read"
          @click="markAsRead"
        >
          Mark as read
        </button>
        <button
          class="text-xs px-3 py-1 rounded text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 tech-button"
          aria-label="Delete notification"
          @click="deleteNotification"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Notification, NotificationCategory } from '~/shared/types/notification'

const props = defineProps<{
  notification: Notification
  compact?: boolean
}>()

const emit = defineEmits<{
  notificationClick: [notification: Notification]
  markAsRead: [id: string]
  delete: [id: string]
}>()

// Category icon mapping
const categoryIcons = {
  system: 'pi-info-circle',
  task: 'pi-check-square',
  membership: 'pi-users',
  admin: 'pi-shield',
  message: 'pi-comment',
} as const

// Get icon for notification category
const categoryIcon = computed(() => {
  const category = props.notification.category as NotificationCategory || 'system'
  return categoryIcons[category] || 'pi-bell'
})

// Determine styling based on notification category and read status
const categoryClasses = computed(() => {
  const priorityClass = getPriorityClass(props.notification.priority)

  return {
    // Add sci-fi styling class
    'notification-sci-fi': true,
    // Add priority-based styling
    [priorityClass]: true,
  }
})

// Get styling class based on priority
function getPriorityClass(priority: string | undefined) {
  switch (priority) {
    case 'high':
      return 'ring-1 ring-red-200 dark:ring-red-900/50'
    case 'low':
      return ''
    case 'normal':
    default:
      return ''
  }
}

// Get border color class based on category
function getBorderClass() {
  const category = props.notification.category as NotificationCategory || 'system'
  switch (category) {
    case 'system':
      return 'bg-primary-500 dark:bg-primary-500'
    case 'membership':
      return 'bg-green-500 dark:bg-green-400'
    case 'task':
      return 'bg-amber-500 dark:bg-amber-400'
    case 'admin':
      return 'bg-red-500 dark:bg-red-400'
    default:
      return 'bg-primary-500 dark:bg-primary-500'
  }
}

// Format timestamp to relative time (e.g., "2 hours ago")
const formattedTime = computed(() => {
  if (!props.notification.createdAt) return ''

  const date = props.notification.createdAt instanceof Date
    ? props.notification.createdAt
    : new Date(props.notification.createdAt)

  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const diffInHours = diffInMs / (1000 * 60 * 60)

  if (diffInHours < 1) {
    const minutes = Math.floor(diffInMs / (1000 * 60))
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
  }
  else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours)
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  }
  else if (diffInHours < 168) { // 7 days
    const days = Math.floor(diffInHours / 24)
    return `${days} day${days !== 1 ? 's' : ''} ago`
  }
  else {
    // Format as date for older notifications
    return date.toLocaleDateString()
  }
})

// Handle click events
const handleClick = () => {
  emit('notificationClick', props.notification)
}

// Mark notification as read
const markAsRead = (event: Event) => {
  event.stopPropagation()
  if (!props.notification.read) {
    emit('markAsRead', props.notification.id)
  }
}

// Delete notification
const deleteNotification = (event: Event) => {
  event.stopPropagation()
  emit('delete', props.notification.id)
}
</script>

<style scoped>
.notification-item {
  transition: all 0.2s ease;
  clip-path: polygon(
    0 0,
    calc(100% - 10px) 0,
    100% 10px,
    100% 100%,
    10px 100%,
    0 calc(100% - 10px)
  );
}

.notification-item:hover {
  transform: translateY(-1px);
}

.notification-glow {
  box-shadow: 0 0 8px currentColor;
}

.notification-pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(57, 255, 20, 0.7);
  }
  50% {
    opacity: 0.7;
    box-shadow: 0 0 0 4px rgba(57, 255, 20, 0);
  }
}

/* Apply tech-button styles from your global tailwind.css */
.tech-button {
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border-radius: var(--tech-border-radius, 0 8px 0 8px);
}

.tech-button:hover {
  box-shadow: 0 0 5px var(--primary-color);
  transform: translateY(-1px);
}
</style>
